import React, { useEffect, useRef, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebase';
import { ensureDailyBackup, localBackupDay, subscribeBackupCatalog, subscribeRestoreReceipt } from '../../utils/accountBackups';

export default function AccountRecovery() {
  const [pending, setPending] = useState(null);
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now());
  const [reloadNeeded, setReloadNeeded] = useState(false);
  const overlay = useRef(null);

  useEffect(() => {
    let stopCatalog;
    let stopReceipt;
    let timer;
    let captureTimer;
    let generation = 0;
    const stopAuth = onAuthStateChanged(auth, user => {
      generation += 1;
      const current = generation;
      stopCatalog?.();
      stopReceipt?.();
      clearInterval(timer);
      clearTimeout(captureTimer);
      setPending(null);
      setError('');
      setReloadNeeded(false);
      if (!user) return;
      let initial = true;
      let restoreId;
      let dailyDate;
      let retryAt = 0;
      let receiptInitialized = false;
      let receiptId;
      stopReceipt = subscribeRestoreReceipt(user.uid, receipt => {
        if (current !== generation) return;
        if (receiptInitialized && receiptId !== receipt?.id) setReloadNeeded(true);
        receiptId = receipt?.id;
        receiptInitialized = true;
      }, () => {
        if (current === generation) setError('Could not monitor account recovery. Reload NoLi before editing.');
      });
      stopCatalog = subscribeBackupCatalog(user.uid, catalog => {
        if (current !== generation) return;
        setPending(catalog.pending?.kind === 'restore' ? catalog.pending : null);
        setNow(Date.now());
        dailyDate = catalog.lastDailyDate;
        if (!initial && restoreId !== catalog.lastRestore?.id) setReloadNeeded(true);
        restoreId = catalog.lastRestore?.id;
        initial = false;
      }, () => {
        if (current === generation) setError('Recovery snapshots are unavailable. Check the connection and Firebase rules in Settings.');
      });
      const capture = async () => {
        setNow(Date.now());
        if (document.visibilityState === 'hidden' || Date.now() < retryAt || dailyDate >= localBackupDay()) return;
        retryAt = Date.now() + 5 * 60 * 1000;
        try {
          await ensureDailyBackup();
          if (current === generation) setError('');
        } catch (failure) {
          if (current === generation) setError(`Daily recovery snapshot was not saved. ${failure.message}`);
        }
      };
      // Keep initial Nutrition/Fitness requests ahead of the once-a-day scan.
      captureTimer = setTimeout(capture, 15000);
      timer = setInterval(capture, 60000);
    });
    return () => {
      generation += 1;
      stopAuth();
      stopCatalog?.();
      stopReceipt?.();
      clearInterval(timer);
      clearTimeout(captureTimer);
    };
  }, []);

  useEffect(() => {
    if (!pending) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [pending]);

  const restoring = pending?.expiresAt > now;
  useEffect(() => {
    if (reloadNeeded && !restoring) window.location.reload();
  }, [reloadNeeded, restoring]);

  useEffect(() => {
    if (!restoring) return undefined;
    const content = document.getElementById('noli-live-content');
    const overflow = document.body.style.overflow;
    if (content) content.inert = true;
    document.body.style.overflow = 'hidden';
    overlay.current?.focus();
    const trapTab = event => { if (event.key === 'Tab') event.preventDefault(); };
    document.addEventListener('keydown', trapTab);
    return () => {
      if (content) content.inert = false;
      document.body.style.overflow = overflow;
      document.removeEventListener('keydown', trapTab);
    };
  }, [restoring]);

  return <>
    {error && !restoring && <div role="status" className="border-b border-[#e0af68]/30 bg-[#e0af68]/10 px-4 py-2 text-xs text-[#e0af68]">{error}</div>}
    {restoring && <div ref={overlay} tabIndex={-1} role="alertdialog" aria-modal="true" aria-labelledby="account-restore-status" className="fixed inset-0 z-[200] flex items-center justify-center bg-[#1a1b26]/95 p-6 text-[#c0caf5]"><div className="max-w-md rounded-xl border border-[#414868] bg-[#24283b] p-6"><h2 id="account-restore-status" className="text-lg font-semibold">Restoring NoLi</h2><p className="mt-3 text-sm text-[#a9b1d6]">Editing is paused while we save a safety copy and restore your snapshot. Keep this tab open. NoLi will reload when finished.</p><p className="mt-3 text-xs text-[#a9b1d6]">If the connection is interrupted, the restore lock expires automatically within two minutes. Reload to check the result before retrying.</p></div></div>}
  </>;
}
